import React, { useState, useMemo } from 'react'
import { Activity, Clock, FileText, Bell, MapPin, User, CalendarDays } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectOption,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
  SearchableSelect,
} from '../components/ui'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import {
  useActivityLogs,
  ACTIVITY_CATEGORY_LABELS,
  ACTIVITY_ACTION_LABELS,
  type ActivityLogFilters,
  type ActivityLog,
  type ActivityCategory,
  type ActivityAction,
} from '../hooks/useActivityLogs'
import { useEmployees } from '../hooks/useEmployees'

export function ActivityLogs() {
  const [filters, setFilters] = useState<ActivityLogFilters>({
    page: 1,
    limit: 20,
    sort: 'DESC',
  })

  const { data, isLoading, error, refetch } = useActivityLogs(filters)
  const { data: employees, isLoading: employeesLoading } = useEmployees({ limit: 10000 })

  const handleFiltersChange = (newFilters: Partial<ActivityLogFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to first page on filter change
    }))
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  // Преобразование сотрудников в формат для SearchableSelect
  const employeeOptions = useMemo(() => {
    if (!employees?.data) return []
    return employees.data.map((emp) => ({
      value: emp.id,
      label: `${emp.last_name} ${emp.first_name}`,
      searchText: `${emp.last_name} ${emp.first_name} ${emp.middle_name || ''} ${emp.employee_number}`,
    }))
  }, [employees?.data])

  // Calculate statistics
  const stats = useMemo(() => {
    if (!data?.data) return null

    const events = data.data
    const attendance = events.filter((e) => e.category === 'attendance').length
    const documents = events.filter((e) => e.category === 'document').length
    const notifications = events.filter((e) => e.category === 'notification').length

    return {
      total: data.total,
      attendance,
      documents,
      notifications,
    }
  }, [data])

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getCategoryBadge = (category: ActivityCategory) => {
    const variants: Record<ActivityCategory, 'default' | 'secondary' | 'outline'> = {
      attendance: 'default',
      document: 'secondary',
      notification: 'outline',
      schedule: 'secondary',
    }
    const icons: Record<ActivityCategory, React.ReactNode> = {
      attendance: <Clock className="h-3 w-3 mr-1" />,
      document: <FileText className="h-3 w-3 mr-1" />,
      notification: <Bell className="h-3 w-3 mr-1" />,
      schedule: <CalendarDays className="h-3 w-3 mr-1" />,
    }
    return (
      <Badge variant={variants[category]} className="flex items-center">
        {icons[category]}
        {ACTIVITY_CATEGORY_LABELS[category]}
      </Badge>
    )
  }

  const getActionLabel = (action: ActivityAction) => {
    return ACTIVITY_ACTION_LABELS[action] || action
  }

  const getDetailsText = (log: ActivityLog) => {
    const { details, category } = log
    if (category === 'attendance' && details.location_name) {
      return (
        <span className="flex items-center text-sm text-muted-foreground">
          <MapPin className="h-3 w-3 mr-1" />
          {details.location_name}
          {details.source_type && (
            <span className="ml-2 text-xs">({details.source_type})</span>
          )}
        </span>
      )
    }
    if (category === 'document' && details.document_title) {
      return (
        <span className="text-sm text-muted-foreground">
          {details.document_title}
          {details.document_type && (
            <span className="text-xs ml-1">({details.document_type})</span>
          )}
        </span>
      )
    }
    if (category === 'notification' && details.notification_title) {
      return (
        <span className="text-sm text-muted-foreground">
          {details.notification_title}
        </span>
      )
    }
    if (category === 'schedule') {
      const isBlocked = log.action === 'shift_limit_blocked'
      const isOverride = log.action === 'shift_limit_override'
      return (
        <div className={`text-sm ${isBlocked ? 'text-red-600' : isOverride ? 'text-amber-600' : 'text-muted-foreground'}`}>
          {details.exception_date && (
            <span className="font-medium">на {details.exception_date}</span>
          )}
          {details.limit_details && <span> — {details.limit_details}</span>}
          {details.performed_by && (
            <span className="block text-xs text-muted-foreground">
              выполнил: {details.performed_by}
            </span>
          )}
        </div>
      )
    }
    return null
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Журнал действий</h1>
          <p className="text-muted-foreground">
            Просмотр и анализ действий пользователей
          </p>
        </div>
        <ErrorState title="Ошибка загрузки журнала действий" onAction={refetch} />
      </div>
    )
  }

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Журнал действий</h1>
        <p className="text-muted-foreground">
          Просмотр и анализ действий пользователей мобильного приложения
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Фильтры</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* Employee Select with Search */}
            <SearchableSelect
              value={filters.employeeId || null}
              onChange={(value) =>
                handleFiltersChange({
                  employeeId: value || undefined,
                })
              }
              options={employeeOptions}
              placeholder="Все сотрудники"
              searchPlaceholder="Поиск сотрудника..."
              emptyOption="Все сотрудники"
              emptySearchText="Сотрудник не найден"
              isLoading={employeesLoading}
            />

            {/* Category */}
            <Select
              value={filters.category || ''}
              onChange={(e) =>
                handleFiltersChange({
                  category: (e.target.value as ActivityCategory) || undefined,
                  action: undefined, // Reset action when category changes
                })
              }
            >
              <SelectOption value="">Все категории</SelectOption>
              <SelectOption value="attendance">Посещаемость</SelectOption>
              <SelectOption value="document">Документы</SelectOption>
              <SelectOption value="notification">Уведомления</SelectOption>
              <SelectOption value="schedule">График</SelectOption>
            </Select>

            {/* Action */}
            <Select
              value={filters.action || ''}
              onChange={(e) =>
                handleFiltersChange({
                  action: (e.target.value as ActivityAction) || undefined,
                })
              }
            >
              <SelectOption value="">Все действия</SelectOption>
              {(!filters.category || filters.category === 'attendance') && (
                <>
                  <SelectOption value="check_in">Приход</SelectOption>
                  <SelectOption value="check_out">Уход</SelectOption>
                  <SelectOption value="break_start">Начало перерыва</SelectOption>
                  <SelectOption value="break_end">Конец перерыва</SelectOption>
                  <SelectOption value="auto_close">Авто-закрытие</SelectOption>
                </>
              )}
              {(!filters.category || filters.category === 'document') && (
                <>
                  <SelectOption value="document_view">Просмотр документа</SelectOption>
                  <SelectOption value="document_created">Создание документа</SelectOption>
                </>
              )}
              {(!filters.category || filters.category === 'notification') && (
                <>
                  <SelectOption value="notification_read">Прочтение уведомления</SelectOption>
                  <SelectOption value="notification_sent">Отправка уведомления</SelectOption>
                </>
              )}
              {(!filters.category || filters.category === 'schedule') && (
                <>
                  <SelectOption value="exception_created">Исключение создано</SelectOption>
                  <SelectOption value="shift_limit_blocked">Блокировка: лимит смен</SelectOption>
                  <SelectOption value="shift_limit_override">Обход лимита админом</SelectOption>
                </>
              )}
            </Select>

            {/* Date From */}
            <Input
              type="date"
              value={filters.dateFrom?.split('T')[0] || ''}
              onChange={(e) =>
                handleFiltersChange({
                  dateFrom: e.target.value
                    ? `${e.target.value}T00:00:00.000Z`
                    : undefined,
                })
              }
              placeholder="Дата с"
            />

            {/* Date To */}
            <Input
              type="date"
              value={filters.dateTo?.split('T')[0] || ''}
              onChange={(e) =>
                handleFiltersChange({
                  dateTo: e.target.value
                    ? `${e.target.value}T23:59:59.999Z`
                    : undefined,
                })
              }
              placeholder="Дата по"
            />
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Всего событий</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.attendance}</p>
                  <p className="text-sm text-muted-foreground">Посещаемость</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.documents}</p>
                  <p className="text-sm text-muted-foreground">Документы</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Bell className="h-4 w-4 text-orange-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.notifications}</p>
                  <p className="text-sm text-muted-foreground">Уведомления</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Журнал событий</CardTitle>
          <CardDescription>
            {data &&
              `Показано ${data.data.length} из ${data.total} событий`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={filters.limit || 20} />
          ) : data?.data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Дата и время</TableHead>
                    <TableHead>Сотрудник</TableHead>
                    <TableHead>Категория</TableHead>
                    <TableHead>Действие</TableHead>
                    <TableHead>Детали</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">
                              {log.employee.last_name} {log.employee.first_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              №{log.employee.employee_number}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getCategoryBadge(log.category)}</TableCell>
                      <TableCell>{getActionLabel(log.action)}</TableCell>
                      <TableCell>{getDetailsText(log)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data && data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                События не найдены
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Попробуйте изменить фильтры поиска или выберите другой период.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
